import { createSignal, onMount, For } from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";

export type KeyboardMode = "text" | "numeric";

type KeyAction = "shift" | "symbols" | "space" | "delete" | "done";
type KeyDef = { label: string; char?: string; action?: KeyAction; w?: number };

const chars = (s: string): KeyDef[] => s.split("").map((c) => ({ label: c, char: c }));

// The app's MSDF font only contains ASCII glyphs, so the keyboard sticks to
// ASCII: accented letters (ã, ç, é…) would render as blanks.
//
// Letters and symbols use the same grid shape, so switching between them
// only changes the labels. The keys stay mounted, and focus stays put.
const LETTERS = [chars("1234567890"), chars("qwertyuiop"), chars("asdfghjkl'"), chars("zxcvbnm-.,")];
const SYMBOLS = [chars("1234567890"), chars("@#&/()_+!?"), chars(":;%*\"=$<>|"), chars("[]{}~^`\\-.")];

const TEXT_ROWS: KeyDef[][] = [
  ...LETTERS,
  [
    { label: "aA", action: "shift", w: 1.5 },
    { label: "#+=", action: "symbols", w: 1.5 },
    { label: "Space", action: "space", w: 3 },
    { label: "Delete", action: "delete", w: 2 },
    { label: "OK", action: "done", w: 2 },
  ],
];

const NUMERIC_ROWS: KeyDef[][] = [
  chars("123"),
  chars("456"),
  chars("789"),
  chars("+0-"),
  [
    { label: "Delete", action: "delete", w: 1.5 },
    { label: "OK", action: "done", w: 1.5 },
  ],
];

const LAYOUT = {
  text: { rows: TEXT_ROWS, pitch: 84 },
  numeric: { rows: NUMERIC_ROWS, pitch: 150 },
};
const KEY_GAP = 8;
const KEY_H = 70;
const ROW_PITCH = 80;

export const keyboardWidth = (mode: KeyboardMode) => {
  const { rows, pitch } = LAYOUT[mode];
  const units = rows[0].reduce((sum, k) => sum + (k.w ?? 1), 0);
  return units * pitch - KEY_GAP;
};

type OnScreenKeyboardProps = {
  mode: KeyboardMode;
  accent: string;
  onChar: (char: string) => void;
  onDelete: () => void;
  onDone: () => void;
  // Back, or Left from the first column: returns focus to the form
  onExit: () => void;
  // Same imperative-handle pattern as SearchKeyboard's keyboardRef
  keyboardRef?: { focus?: () => void };
};

const OnScreenKeyboard = (props: OnScreenKeyboardProps) => {
  const { rows, pitch } = LAYOUT[props.mode];
  const refs: any[][] = rows.map(() => []);
  const [focused, setFocused] = createSignal<string | null>(null);
  const [shift, setShift] = createSignal(false);
  const [symbols, setSymbols] = createSignal(false);

  // Key positions in grid units, used to find the nearest key when moving
  // between rows that have different key widths
  const layoutRows = rows.map((row) => {
    let x = 0;
    return row.map((key) => {
      const w = key.w ?? 1;
      const pos = { key, x, w };
      x += w;
      return pos;
    });
  });

  const charAt = (r: number, i: number) => {
    if (props.mode === "numeric" || r >= LETTERS.length) return rows[r][i].char;
    const c = (symbols() ? SYMBOLS : LETTERS)[r][i].char!;
    return shift() ? c.toUpperCase() : c;
  };

  const press = (r: number, i: number) => {
    const key = rows[r][i];
    switch (key.action) {
      case "shift": setShift((s) => !s); break;
      case "symbols": setSymbols((s) => !s); break;
      case "space": props.onChar(" "); break;
      case "delete": props.onDelete(); break;
      case "done": props.onDone(); break;
      default: props.onChar(charAt(r, i)!);
    }
  };

  const moveVertical = (r: number, i: number, dir: 1 | -1) => {
    const target = layoutRows[r + dir];
    if (!target) return;
    const from = layoutRows[r][i];
    const center = from.x + from.w / 2;
    let best = 0;
    target.forEach((pos, j) => {
      const b = target[best];
      if (Math.abs(pos.x + pos.w / 2 - center) < Math.abs(b.x + b.w / 2 - center)) best = j;
    });
    refs[r + dir][best]?.setFocus();
  };

  onMount(() => {
    if (props.keyboardRef) props.keyboardRef.focus = () => refs[0][0]?.setFocus();
  });

  return (
    <View width={keyboardWidth(props.mode)} height={rows.length * ROW_PITCH}>
      <For each={layoutRows}>
        {(row, r) => (
          <View y={r() * ROW_PITCH}>
            <For each={row}>
              {(pos, i) => {
                const id = `${r()}-${i()}`;
                const isFocused = () => focused() === id;
                const isOn = () =>
                  (pos.key.action === "shift" && shift()) || (pos.key.action === "symbols" && symbols());
                const isDone = pos.key.action === "done";
                const width = pos.w * pitch - KEY_GAP;
                return (
                  <View
                    ref={(el: any) => { refs[r()][i()] = el; }}
                    x={pos.x * pitch}
                    width={width}
                    height={KEY_H}
                    borderRadius={12}
                    color={hexColor(
                      isFocused() ? "#FFFFFF" : isOn() || isDone ? props.accent + "99" : "#FFFFFF14"
                    )}
                    onFocus={() => setFocused(id)}
                    onBlur={() => setFocused((f) => (f === id ? null : f))}
                    onEnter={() => { press(r(), i()); return true; }}
                    onLeft={() => {
                      if (i() === 0) props.onExit();
                      else refs[r()][i() - 1]?.setFocus();
                      return true;
                    }}
                    onRight={() => { refs[r()][i() + 1]?.setFocus(); return true; }}
                    onUp={() => { moveVertical(r(), i(), -1); return true; }}
                    onDown={() => { moveVertical(r(), i(), 1); return true; }}
                    onBackspace={() => { props.onDelete(); return true; }}
                    onEscape={() => { props.onExit(); return true; }}
                  >
                    <Text
                      y={pos.key.action ? 24 : 18}
                      contain="width"
                      textAlign="center"
                      style={{
                        width,
                        fontSize: pos.key.action ? 22 : 30,
                        fontWeight: pos.key.action ? "bold" : "normal",
                      }}
                      // A prop, not in style: style values aren't updated after the first render
                      color={hexColor(isFocused() ? "#000000" : "#FFFFFF")}
                    >
                      {pos.key.action ? pos.key.label : charAt(r(), i())}
                    </Text>
                  </View>
                );
              }}
            </For>
          </View>
        )}
      </For>
    </View>
  );
};

export default OnScreenKeyboard;
